import { useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { GENERATE_CHAT } from '@/lib/operations/generate'
import { useGenerateStore } from '@/state/generateStore'
import { apolloClient } from '@/lib/queryClient'
import { useLangGraphStream } from '@/hooks/useLangGraphStream'
import type { NodeName, NodeStatus, StreamPublishQueueItem } from '@/lib/types'

// ─── Chat generation ──────────────────────────────────────────────────────────

interface GenerateChatInput {
  clientId: string
  message: string
  contentType: string
}

interface GenerateChatResult {
  content: string
  contentType: string
}

export function useGenerateChat() {
  const [mutate, { loading }] = useMutation<{ generate: GenerateChatResult }>(GENERATE_CHAT)
  const [result, setResult] = useState<GenerateChatResult | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const generate = async (input: GenerateChatInput) => {
    setError(null)
    try {
      const res = await mutate({ variables: { input } })
      const data = res.data?.generate ?? null
      setResult(data)
      return data
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
    }
  }

  return { generate, loading, result, error }
}

// ─── SSE helpers ──────────────────────────────────────────────────────────────

function dispatchSSEEvent(
  eventName: string,
  data: string,
  store: ReturnType<typeof useGenerateStore.getState>,
) {
  if (eventName === 'token' || eventName === 'data') {
    store.appendBuffer(data)
    return
  }

  if (eventName === 'node_start' || eventName === 'node_end') {
    try {
      const parsed = JSON.parse(data) as { node?: string }
      if (parsed.node) {
        const status: NodeStatus = eventName === 'node_start' ? 'active' : 'done'
        store.setNodeStatus(parsed.node as NodeName, status)
      }
    } catch {
      // Ignore malformed JSON
    }
    return
  }

  if (eventName === 'node_update') {
    try {
      const parsed = JSON.parse(data) as { node?: string; status?: string; data?: Record<string, unknown> }
      if (parsed.node) {
        const nodeStatus: NodeStatus = parsed.status === 'complete' ? 'done' : 'active'
        store.setNodeStatus(parsed.node as NodeName, nodeStatus)

        if (parsed.data) {
          const usage = (parsed.data as { usage_metadata?: { input_tokens?: number; output_tokens?: number; total_tokens?: number } }).usage_metadata
          if (usage && typeof usage === 'object') store.addTokenUsage(usage)

          const failureReason = (parsed.data as { pipeline_failure_reason?: string }).pipeline_failure_reason
          if (failureReason) {
            store.setAgentStatus('error')
            store.setErrorMessage(failureReason)
          }
        }

        if (parsed.node === 'resolve_client' && parsed.data) {
          const model = (parsed.data as { active_model?: string }).active_model
          if (model) store.setActiveModel(model)
        }

        if (parsed.node === 'strategy_planner' && parsed.data) {
          const plan = (parsed.data as { channel_plan?: Record<string, unknown> }).channel_plan
          if (plan) store.setChannelPlan(plan as Parameters<typeof store.setChannelPlan>[0])
        }

        if (parsed.node === 'campaign_writer' && parsed.data) {
          const slots = (parsed.data as { content_schedule?: Array<{ slot_id: string; channel: string; content: string | null }> }).content_schedule
          if (slots) store.setContentSlots(slots)
        }

        if (parsed.node === 'output_formatter' && parsed.data) {
          const queue = (parsed.data as { publish_queue?: StreamPublishQueueItem[] }).publish_queue
          if (Array.isArray(queue)) store.setPublishQueue(queue)
        }
      }
    } catch {
      // Ignore malformed JSON
    }
    return
  }

  if (eventName === 'thread') {
    try {
      const parsed = JSON.parse(data) as {
        thread_id?: string
        checkpoint_id?: string
      }
      if (parsed.thread_id) store.setThreadId(parsed.thread_id)
      if (parsed.checkpoint_id) store.setCheckpointId(parsed.checkpoint_id)
    } catch {
      // Ignore malformed JSON
    }
    return
  }

  if (eventName === 'status') {
    try {
      const parsed = JSON.parse(data) as { status?: string; run_id?: string }
      if (parsed.run_id) store.setRunId(parsed.run_id)
    } catch {
      // Ignore malformed JSON
    }
    return
  }

  if (eventName === 'done') {
    if (store.agentStatus !== 'error') {
      store.setAgentStatus('complete')
    }
    void apolloClient.refetchQueries({ include: ['GetClient'] })
    return
  }

  if (eventName === 'error') {
    store.setAgentStatus('error')
    try {
      const outer = JSON.parse(data) as { message?: string }
      const rawMsg = outer.message ?? data
      try {
        const inner = JSON.parse(rawMsg) as { message?: string; error?: string }
        store.setErrorMessage(inner.message ?? inner.error ?? rawMsg)
      } catch {
        store.setErrorMessage(rawMsg)
      }
    } catch {
      store.setErrorMessage(data)
    }
    return
  }
}

// ─── Campaign SSE streaming ───────────────────────────────────────────────────

export function useStreamCampaign() {
  const { stream: streamSSE, isStreaming } = useLangGraphStream({
    onEvent: (type, data) => dispatchSSEEvent(type, data, useGenerateStore.getState()),
    onDone: () => { /* SSE done event handles completion */ },
    onError: () => useGenerateStore.getState().setAgentStatus('error'),
  })

  const stream = async (clientId: string, runId?: string) => {
    const store = useGenerateStore.getState()
    store.reset()
    store.setAgentStatus('running')
    if (runId) store.setRunId(runId)

    await streamSSE(
      `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/content-stream`,
      { run_id: runId },
    )
  }

  return { stream, isStreaming }
}

// ─── Resume SSE streaming ─────────────────────────────────────────────────────

export function useResumeStream() {
  const { stream: streamSSE, isStreaming } = useLangGraphStream({
    onEvent: (type, data) => dispatchSSEEvent(type, data, useGenerateStore.getState()),
    onDone: () => { /* SSE done event handles completion */ },
    onError: () => useGenerateStore.getState().setAgentStatus('error'),
  })

  const resume = async (
    clientId: string,
    threadId: string,
    checkpointId: string,
  ) => {
    const store = useGenerateStore.getState()
    store.reset()
    store.setAgentStatus('running')

    await streamSSE(
      `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/content-resume`,
      { thread_id: threadId, checkpoint_id: checkpointId },
    )
  }

  return { resume, isStreaming }
}
